import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Match3Game from './templates/Match3Game';
import TriviaShowdownGame from './templates/TriviaShowdownGame';
import MemoryGridGame from './templates/MemoryGridGame';
import WordPuzzleGame from './templates/WordPuzzleGame';
import SpeedQuizGame from './templates/SpeedQuizGame';
import CardBattlerGame from './templates/CardBattlerGame';
import TowerDefenseGame from './templates/TowerDefenseGame';
import { generateGameContent, getGameTemplate } from '../../services/gameService';
import { completeGame, addXP, getGameContent, saveGameContent } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import './GameEngine.css';

function GameEngine() {
    const location = useLocation();
    const navigate = useNavigate();

    const { currentUser } = useAuth();

    // Get game parameters from state or localStorage (for refresh support)
    const stateParams = location.state || {};
    const [gameParams] = useState(() => {
        if (stateParams.templateId && stateParams.topic) {
            // Save to localStorage for refresh support
            localStorage.setItem('currentGameParams', JSON.stringify(stateParams));
            return stateParams;
        }
        // Try to recover from localStorage
        try {
            const saved = localStorage.getItem('currentGameParams');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    const {
        templateId = 'match3',
        topic = 'General Knowledge',
        moduleTitle = '',
        concepts = [],
        difficulty = 'intermediate',
        worldNodeId = ''
    } = gameParams;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [gameContent, setGameContent] = useState(null);
    const [template, setTemplate] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Initializing game...');
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isCached, setIsCached] = useState(false);
    const progressIntervalRef = useRef(null);

    const loadGame = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setLoadingProgress(5);

            // Load game template
            setLoadingMessage('Loading game template...');
            setLoadingProgress(10);
            const templateData = await getGameTemplate(templateId);
            setTemplate(templateData.template);
            setLoadingProgress(20);

            // Check cache first!
            setLoadingMessage('Checking for cached content...');
            const cachedResult = await getGameContent(
                currentUser?.uid,
                topic,
                templateId,
                moduleTitle,
                difficulty
            );

            if (cachedResult.success && cachedResult.data) {
                // Cache hit! Load instantly
                console.log('🚀 Game content loaded from cache - INSTANT!');
                setIsCached(true);
                setLoadingProgress(100);
                setLoadingMessage('Loading from cache...');
                setGameContent(cachedResult.data);
                setTimeout(() => setLoading(false), 300);
                return;
            }

            // Cache miss - generate new content with progress simulation
            setLoadingMessage('AI is creating your game content...');
            setLoadingProgress(25);

            // Start progress simulation for AI generation
            progressIntervalRef.current = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev >= 85) return prev;
                    return prev + Math.random() * 8;
                });
            }, 500);

            const contentData = await generateGameContent(
                templateId,
                topic,
                moduleTitle,
                concepts,
                difficulty
            );

            // Stop progress simulation
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }

            setLoadingProgress(95);
            setGameContent(contentData.gameContent);

            // Save to cache for next time (don't await - do in background)
            if (currentUser?.uid) {
                saveGameContent(
                    currentUser.uid,
                    topic,
                    templateId,
                    moduleTitle,
                    difficulty,
                    contentData.gameContent
                ).catch(err => console.error('Cache save error:', err));
            }

            setLoadingProgress(100);
            setLoadingMessage('Ready to play!');

            setTimeout(() => setLoading(false), 500);

        } catch (err) {
            console.error('Error loading game:', err);
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
            setError('Failed to load game. Please try again.');
            setLoading(false);
        }
    }, [templateId, topic, moduleTitle, concepts, difficulty, currentUser?.uid]);

    useEffect(() => {
        loadGame();
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, [loadGame]);

    const handleGameComplete = async (results) => {
        console.log('Game completed:', results);

        // Save game completion to Firebase
        if (currentUser && worldNodeId && results.victory) {
            try {
                await completeGame(currentUser.uid, topic, worldNodeId, {
                    score: results.score,
                    stars: results.stars,
                    xp: results.xp
                });

                // Add XP reward
                if (results.xp > 0) {
                    await addXP(currentUser.uid, results.xp, 'game');
                }

                console.log('✅ Game progress saved successfully!');
            } catch (error) {
                console.error('Error saving game progress:', error);
            }
        }
    };

    const handleQuit = () => {
        navigate('/world-map', { state: { topic } });
    };

    const renderGame = () => {
        switch (templateId) {
            case 'match3':
                return (
                    <Match3Game
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'trivia':
                return (
                    <TriviaShowdownGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'memory':
                return (
                    <MemoryGridGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'wordpuzzle':
                return (
                    <WordPuzzleGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'speedquiz':
                return (
                    <SpeedQuizGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'cardbattle':
                return (
                    <CardBattlerGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            case 'towerdefense':
                return (
                    <TowerDefenseGame
                        gameContent={gameContent}
                        template={template}
                        topic={topic}
                        difficulty={difficulty}
                        onComplete={handleGameComplete}
                        onQuit={handleQuit}
                    />
                );
            default:
                return <div className="game-error">Unknown game type</div>;
        }
    };

    if (loading) {
        return (
            <div className="game-engine-loading">
                <div className="loading-container">
                    <div className="loading-icon">
                        {templateId === 'match3' && '🧩'}
                        {templateId === 'trivia' && '⚡'}
                        {templateId === 'memory' && '🧠'}
                        {templateId === 'wordpuzzle' && '📝'}
                        {templateId === 'speedquiz' && '🏎️'}
                        {templateId === 'cardbattle' && '🃏'}
                        {templateId === 'towerdefense' && '🏰'}
                    </div>
                    <h2 className="loading-title">{template?.name || 'Loading Game'}</h2>
                    <p className="loading-message">{loadingMessage}</p>
                    <div className="loading-bar">
                        <div
                            className="loading-progress-fill"
                            style={{ width: `${loadingProgress}%` }}
                        />
                    </div>
                    <div className="loading-percent">{Math.round(loadingProgress)}%</div>
                    {isCached && (
                        <div className="loading-cached-badge">⚡ Instant Load</div>
                    )}
                    <div className="loading-topic">
                        <span>📚</span> {topic}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="game-engine-error">
                <div className="error-container">
                    <span className="error-icon">😕</span>
                    <h2>Oops!</h2>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button className="btn-retry" onClick={loadGame}>
                            🔄 Try Again
                        </button>
                        <button className="btn-back" onClick={handleQuit}>
                            🗺️ Back to Map
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="game-engine">
            {renderGame()}
        </div>
    );
}

export default GameEngine;
